"use strict";
//
// Copyright © 2020, 2021 Anticrm Platform Contributors.
// Copyright © 2021 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.addStringsLoader = addStringsLoader;
exports.loadPluginStrings = loadPluginStrings;
exports.translate = translate;
exports.translateCB = translateCB;
const intl_messageformat_1 = require("intl-messageformat");
const event_1 = require("./event");
const ident_1 = require("./ident");
const platform_1 = __importStar(require("./platform"));
const status_1 = require("./status");
const metadata_1 = require("./metadata");
const loaders = new Map();
const translations = new Map();
const cache = new Map();
const englishTranslationsForMissing = new Map();
/**
 * @public
 * @param plugin -
 * @param loader -
 */
function addStringsLoader(plugin, loader) {
    loaders.set(plugin, loader);
}
/**
 * Perform load of all internationalization sources for all plugins available.
 * @public
 */
async function loadPluginStrings(locale, force = false) {
    if (force) {
        cache.clear();
    }
    for (const [plugin] of loaders) {
        const localtTanslations = translations.get(locale) ?? new Map();
        if (!translations.has(locale)) {
            translations.set(locale, localtTanslations);
        }
        let messages = localtTanslations.get(plugin);
        if (messages === undefined || force) {
            messages = await loadTranslationsForComponent(plugin, locale);
            localtTanslations.set(plugin, messages);
        }
    }
}
async function setStatus(status, skipError) {
    if (skipError !== true) {
        await (0, event_1.setPlatformStatus)(status);
    }
}
/** Notify platform and return a Status for load/resolve paths that do not use the per-message format cache. */
async function pipelineErrorToStatus(err, skipError) {
    const status = (0, status_1.unknownError)(err);
    await setStatus(status, skipError);
    return status;
}
/**
 * On compile/resolve failure: cache failure for this intl id, notify platform, return `message` as UI fallback.
 */
async function handleIntlPipelineFailure(err, message, localeCache, skipError) {
    const status = (0, status_1.unknownError)(err);
    localeCache.set(message, status);
    await setStatus(status, skipError);
    return message;
}
async function loadTranslationsForComponent(plugin, locale, skipError) {
    const loader = loaders.get(plugin);
    if (loader === undefined) {
        const status = new status_1.Status(status_1.Severity.ERROR, platform_1.default.status.NoLoaderForStrings, { plugin });
        await setStatus(status, skipError);
        return status;
    }
    try {
        return (await loader(locale));
    }
    catch (err) {
        console.error('No translations found for plugin', plugin, err);
        try {
            return (await loader('en'));
        }
        catch (err) {
            return await pipelineErrorToStatus(err, skipError);
        }
    }
}
function getCachedTranslation(id, locale) {
    const localtTanslations = translations.get(locale);
    if (localtTanslations === undefined) {
        return undefined;
    }
    const messages = localtTanslations.get(id.component);
    if (messages === undefined) {
        return undefined;
    }
    if (messages instanceof status_1.Status) {
        return messages;
    }
    if (id.kind !== undefined) {
        if (messages[id.kind]?.[id.name] !== undefined) {
            return messages[id.kind]?.[id.name];
        }
    }
}
async function getTranslation(id, locale, skipError) {
    try {
        const localtTanslations = translations.get(locale) ?? new Map();
        if (!translations.has(locale)) {
            translations.set(locale, localtTanslations);
        }
        let messages = localtTanslations.get(id.component);
        if (messages === undefined) {
            messages = await loadTranslationsForComponent(id.component, locale, skipError);
            localtTanslations.set(id.component, messages);
        }
        if (messages instanceof status_1.Status) {
            return messages;
        }
        if (id.kind !== undefined) {
            if (messages[id.kind]?.[id.name] !== undefined) {
                return messages[id.kind]?.[id.name];
            }
            else {
                let eng = englishTranslationsForMissing.get(id.component);
                if (eng === undefined) {
                    eng = await loadTranslationsForComponent(id.component, 'en', skipError);
                    englishTranslationsForMissing.set(id.component, eng);
                }
                if (eng instanceof status_1.Status) {
                    return eng;
                }
                return eng[id.kind]?.[id.name];
            }
        }
        else {
            return messages[id.name];
        }
    }
    catch (err) {
        return await pipelineErrorToStatus(err, skipError);
    }
}
/**
 * @public
 * @param message -
 * @param params -
 * @returns
 */
async function translate(message, params, language, skipError) {
    const locale = language ?? (0, metadata_1.getMetadata)(platform_1.default.metadata.locale) ?? 'en';
    const localCache = cache.get(locale) ?? new Map();
    if (!cache.has(locale)) {
        cache.set(locale, localCache);
    }
    const compiled = localCache.get(message);
    if (compiled !== undefined) {
        if (compiled instanceof status_1.Status) {
            return message;
        }
        try {
            return compiled.format(params);
        }
        catch (err) {
            const status = (0, status_1.unknownError)(err, { ...params, msg: message, language });
            await setStatus(status, skipError);
            return message;
        }
    }
    try {
        const id = (0, ident_1._parseId)(message);
        if (id.component === platform_1._EmbeddedId) {
            return id.name;
        }
        const translation = getCachedTranslation(id, locale) ?? (await getTranslation(id, locale, skipError)) ?? message;
        if (translation instanceof status_1.Status) {
            localCache.set(message, translation);
            return message;
        }
        const compiledNew = new intl_messageformat_1.IntlMessageFormat(translation, locale, undefined, { ignoreTag: true });
        localCache.set(message, compiledNew);
        try {
            return compiledNew.format(params);
        }
        catch (err) {
            const status = (0, status_1.unknownError)(err, { ...params, msg: message, language });
            await setStatus(status, skipError);
            return message;
        }
    }
    catch (err) {
        return await handleIntlPipelineFailure(err, message, localCache, skipError);
    }
}
/**
 * Will do a translation in case language file already in cache, a translate is called and Promise is returned overwise
 */
function translateCB(message, params, language, resolve, skipError) {
    const locale = language ?? (0, metadata_1.getMetadata)(platform_1.default.metadata.locale) ?? 'en';
    const localCache = cache.get(locale) ?? new Map();
    if (!cache.has(locale)) {
        cache.set(locale, localCache);
    }
    const compiled = localCache.get(message);
    if (compiled !== undefined) {
        if (compiled instanceof status_1.Status) {
            resolve(message);
            return;
        }
        try {
            resolve(compiled.format(params));
        }
        catch (err) {
            const status = (0, status_1.unknownError)(err);
            void setStatus(status, skipError);
            resolve(message);
        }
        return;
    }
    let id;
    try {
        id = (0, ident_1._parseId)(message);
        if (id.component === platform_1._EmbeddedId) {
            resolve(id.name);
            return;
        }
    }
    catch (err) {
        void handleIntlPipelineFailure(err, message, localCache, skipError).then(resolve);
        return;
    }
    const translation = getCachedTranslation(id, locale);
    if (translation === undefined || translation instanceof status_1.Status) {
        void translate(message, params, language, skipError)
            .then((res) => {
            resolve(res);
        })
            .catch((err) => {
            const status = (0, status_1.unknownError)(err);
            void setStatus(status, skipError);
            resolve(message);
        });
        return;
    }
    let compiledNew;
    try {
        compiledNew = new intl_messageformat_1.IntlMessageFormat(translation, locale, undefined, { ignoreTag: true });
        localCache.set(message, compiledNew);
    }
    catch (err) {
        void handleIntlPipelineFailure(err, message, localCache, skipError).then(resolve);
        return;
    }
    try {
        resolve(compiledNew.format(params));
    }
    catch (err) {
        const status = (0, status_1.unknownError)(err);
        void setStatus(status, skipError);
        resolve(message);
    }
}
//# sourceMappingURL=i18n.js.map